/**
 * Image-forward persona tile for the /for-investors callback grid.
 *
 * Modeled on the mens-health PersonaCard: a gradient + icon render
 * immediately; real photography at `/personas/{id}.jpg` overlays on top
 * when the file exists. Drop a photo into frontend/public/personas/ and the
 * tile upgrades itself — no code change. If the <img> 404s (no photo yet),
 * onError flips `imgOk` false and we fall back to the gradient cleanly.
 *
 * Clicking the card selects the persona (opens the callback form). Adapted
 * to the VentureOS apple-light palette rather than mens-health's dark/gold.
 */
import React, { useState } from 'react';
import clsx from 'clsx';
import type { InvestorPersona } from '../data/investorPersonas';

interface Props {
  persona: InvestorPersona;
  selected: boolean;
  onSelect: (p: InvestorPersona) => void;
}

const PersonaCard: React.FC<Props> = ({ persona, selected, onSelect }) => {
  const Icon = persona.icon;
  const [imgOk, setImgOk] = useState(true);

  return (
    <button
      type="button"
      onClick={() => onSelect(persona)}
      data-persona-id={persona.id}
      aria-pressed={selected}
      aria-label={`Choose ${persona.name} — ${persona.tagline}`}
      className={clsx(
        'group relative aspect-[4/5] w-full overflow-hidden rounded-3xl border bg-gradient-to-br text-left transition-all duration-200',
        persona.gradient,
        // Flagship gets a subtle scale + stronger ring so it reads as the
        // centerpiece even before selection.
        persona.flagship && 'sm:scale-[1.04] sm:shadow-card-hover',
        selected
          ? 'border-apple-blue ring-2 ring-apple-blue shadow-card-hover'
          : clsx(
              'hover:border-apple-blue/60 hover:shadow-card-hover',
              persona.flagship ? 'border-apple-blue/40 ring-1 ring-apple-blue/30' : 'border-apple-divider'
            )
      )}
    >
      {/* Real photo overlays the gradient when it exists; falls back on 404. */}
      {imgOk && (
        <img
          src={`/personas/${persona.id}.${persona.ext}`}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover transition-opacity group-hover:opacity-95"
          onError={() => setImgOk(false)}
        />
      )}

      {/* Fallback icon — only shown while there's no photo. */}
      {!imgOk && (
        <Icon
          className="absolute right-5 top-5 h-9 w-9 text-white/80 drop-shadow"
          aria-hidden="true"
          strokeWidth={1.25}
        />
      )}

      {/* Vignette so the name stays legible over busy photography. */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

      {persona.flagship && (
        <span className="absolute left-4 top-4 z-20 inline-flex items-center rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-apple-blue shadow-sm">
          Flagship
        </span>
      )}

      <div className="relative z-10 flex h-full flex-col justify-end p-5">
        <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-white/80">
          {persona.tagline}
        </p>
        <h3 className="mt-1 text-2xl font-semibold tracking-tight text-white">
          {persona.name}
        </h3>
        <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-white">
          Have {persona.name} call me
          <span aria-hidden="true" className="transition-transform group-hover:translate-x-0.5">
            →
          </span>
        </span>
      </div>
    </button>
  );
};

export default PersonaCard;
